// Mapbox token will be imported dynamically where needed
import type { ItineraryItem } from './itinerary';
import type { LocationCoordinates } from './location';
import { geocodeAddress } from './maps';
import {
  buildPointGeofence,
  startGeofenceMonitoring,
  type GeofenceEvent,
} from './geofencing';
import { haversineDistance } from '@/utils/geocodeHelpers';
import {
  isAggressiveMapboxCostMode,
  recordMapboxCostError,
  tryConsumeMapboxBudget,
} from '@/config/mapboxCostPolicy';

const MAX_NAV_PROMPT_KM = 80;
const MAX_NAV_TRAVEL_MINUTES = 240;

export type TravelMode = 'walking' | 'driving' | 'transit';

// Mapbox documented max route lengths (approximate, keep conservative)
const MODE_DISTANCE_LIMIT_METERS: Record<TravelMode, number> = {
  walking: 50_000,   // ~50 km
  driving: 600_000,  // ~600 km
  transit: 600_000,  // uses driving profile under the hood
};

export type RouteStep = {
  instruction: string;
  distance: number; // meters
  duration: number; // seconds
  maneuver: {
    type: string;
    location: [number, number]; // [longitude, latitude]
    instruction?: string;
  };
};

export type DetailedRoute = {
  mode: TravelMode;
  duration: number; // seconds
  distance: number; // meters
  geometry: LocationCoordinates[]; // Full route path
  steps: RouteStep[]; // Turn-by-turn instructions
  summary?: string; // Route summary
};

export type RouteOption = {
  mode: TravelMode;
  duration: number; // in seconds
  distance: number; // in meters
  instructions?: string;
};

export type NavigationPrompt = {
  nextActivity: ItineraryItem;
  currentLocation: LocationCoordinates;
  routeOptions: RouteOption[];
  recommendedDepartureTime: Date;
  fastestRoute: RouteOption;
  travelDurationMinutes: number;
  leaveInMinutes: number;
  arrivalEta: Date;
  activityStartDateTime: Date;
  bufferMinutes: number;
  message: string;
};

const isValidCoordinate = (coord?: LocationCoordinates | null): coord is LocationCoordinates => {
  if (!coord) return false;
  const { latitude, longitude } = coord;
  return (
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180 &&
    !(latitude === 0 && longitude === 0)
  );
};

/**
 * Calculate detailed route between two points using Mapbox Directions API
 * Returns full route geometry and step-by-step instructions
 * @param departureTime Optional departure time for traffic-aware routing
 */
export const calculateDetailedRoute = async (
  start: LocationCoordinates,
  end: LocationCoordinates,
  mode: TravelMode = 'driving',
  departureTime?: Date
): Promise<DetailedRoute | null> => {
  try {
    if (!isValidCoordinate(start) || !isValidCoordinate(end)) {
      console.warn('[Navigation] Skipping route calculation due to invalid coordinates', {
        start,
        end,
      });
      return null;
    }

    // Guard against Mapbox max-distance limits to avoid 422 spam when user is far away
    try {
      const distanceKm = haversineDistance(
        { lat: start.latitude, lng: start.longitude },
        { lat: end.latitude, lng: end.longitude }
      );
      const distanceMeters = distanceKm * 1000;
      const maxDistance = MODE_DISTANCE_LIMIT_METERS[mode] ?? MODE_DISTANCE_LIMIT_METERS.driving;
      if (distanceMeters > maxDistance) {
        console.warn('[Navigation] Skipping route: distance exceeds Mapbox limit for mode', {
          mode,
          distanceKm: distanceKm.toFixed(1),
          maxDistanceKm: (maxDistance / 1000).toFixed(1),
          start,
          end,
        });
        return null;
      }
    } catch (err) {
      // Non-fatal; continue without guard if distance calc fails
      console.warn('[Navigation] Distance precheck failed; continuing', err);
    }

    const { getMapboxAccessTokenSync } = await import('@/config/mapbox');
    const accessToken = getMapboxAccessTokenSync();
    if (!accessToken) {
      console.warn('[Navigation] Mapbox access token missing; skipping route calculation.');
      return null;
    }
    const baseProfile = mode === 'transit' ? 'driving' : mode; // We approximate transit using driving.

    // Normalize coordinates to fixed precision to avoid invalid query params
    const formatCoord = (coord: number) => Number(coord.toFixed(6));
    const startLng = formatCoord(start.longitude);
    const startLat = formatCoord(start.latitude);
    const endLng = formatCoord(end.longitude);
    const endLat = formatCoord(end.latitude);

    // Mapbox traffic-aware routing uses the driving-traffic profile + depart_at (ISO 8601),
    // NOT departure_time. Supplying an unknown query param returns 422 InvalidInput.
    const shouldUseTraffic = Boolean(departureTime && mode === 'driving');
    const profileForRequest = shouldUseTraffic ? 'driving-traffic' : baseProfile;
    const directionsBudget = tryConsumeMapboxBudget('directions');
    if (!directionsBudget.allowed) {
      if (__DEV__) {
        console.warn('[Navigation] Directions blocked by cost policy:', directionsBudget.reason);
      }
      return null;
    }

    const encodedToken = encodeURIComponent(accessToken);
    let url = `https://api.mapbox.com/directions/v5/mapbox/${profileForRequest}/${startLng},${startLat};${endLng},${endLat}?access_token=${encodedToken}&geometries=geojson&steps=true&overview=full`;

    // Add depart_at for traffic-aware routing (must not be in the past)
    if (shouldUseTraffic && departureTime) {
      const now = Date.now();
      const safeDeparture = departureTime.getTime() < now ? new Date(now) : departureTime;
      const departAtIso = safeDeparture.toISOString().replace(/\.\d{3}Z$/, 'Z');
      url += `&depart_at=${encodeURIComponent(departAtIso)}`;
    }

    // Avoid pointless API call when start and end are effectively the same spot
    const sameLocation =
      Math.abs(start.latitude - end.latitude) < 0.0001 && Math.abs(start.longitude - end.longitude) < 0.0001;
    if (sameLocation) {
      console.warn('[Navigation] Start and destination are identical; skipping route.');
      return null;
    }

    const response = await fetch(url);
    const data = await response.json();

    if (!response.ok) {
      const apiMessage = data?.message || data?.error || response.statusText;
      console.error('[Navigation] Route API error', {
        status: response.status,
        apiMessage,
        data,
        start: { lat: startLat, lng: startLng },
        end: { lat: endLat, lng: endLng },
        profile: profileForRequest,
      });
      // Common Mapbox 422 causes: identical coordinates, off-world coords, or missing token scopes
      return null;
    }
    
    if (__DEV__) {
      console.log('[Navigation] Route API response received');
    }
    
    if (data.routes && data.routes.length > 0) {
      const route = data.routes[0];
      const leg = route.legs?.[0];
      
      // Extract route geometry
      const geometry: LocationCoordinates[] = [];
      if (route.geometry) {
        // Mapbox returns geometry as GeoJSON LineString with coordinates array
        // Format: { type: 'LineString', coordinates: [[lng, lat], [lng, lat], ...] }
        if (route.geometry.type === 'LineString' && Array.isArray(route.geometry.coordinates)) {
          route.geometry.coordinates.forEach((coord: [number, number]) => {
            geometry.push({
              latitude: coord[1],
              longitude: coord[0],
            });
          });
        } else if (Array.isArray(route.geometry)) {
          // Fallback: if it's already an array of coordinates
          route.geometry.forEach((coord: [number, number]) => {
            geometry.push({
              latitude: coord[1],
              longitude: coord[0],
            });
          });
        }
      }
      
      if (__DEV__) {
        console.log(`[Navigation] Extracted ${geometry.length} coordinates from route`);
      }
      
      // Extract step-by-step instructions
      const steps: RouteStep[] = [];
      if (leg?.steps && Array.isArray(leg.steps)) {
        leg.steps.forEach((step: any) => {
          const instruction = step.maneuver?.instruction || 
                            step.maneuver?.modifier + ' ' + step.maneuver?.type || 
                            step.maneuver?.type || 
                            'Continue';
          
          steps.push({
            instruction: instruction.trim() || 'Continue',
            distance: step.distance || 0,
            duration: step.duration || 0,
            maneuver: {
              type: step.maneuver?.type || 'turn',
              location: step.maneuver?.location || 
                       (step.geometry?.coordinates?.[0] ? [step.geometry.coordinates[0][0], step.geometry.coordinates[0][1]] : [0, 0]),
              instruction: step.maneuver?.instruction,
            },
          });
        });
      }
      
      if (__DEV__) {
        console.log(`[Navigation] Extracted ${steps.length} steps from route`);
      }
      
      return {
        mode,
        duration: route.duration || 0,
        distance: route.distance || 0,
        geometry,
        steps,
        summary: `${Math.round((route.distance || 0) / 1000)} km, ${Math.round((route.duration || 0) / 60)} min`,
      };
    }

    return null;
  } catch (error) {
    recordMapboxCostError('directions');
    console.error('[Navigation] Error calculating detailed route:', error);
    return null;
  }
};

/**
 * Calculate route between two points using Mapbox Directions API
 * @param departureTime Optional departure time for traffic-aware routing
 */
export const calculateRoute = async (
  start: LocationCoordinates,
  end: LocationCoordinates,
  mode: TravelMode = 'driving',
  departureTime?: Date
): Promise<RouteOption | null> => {
  try {
    const detailedRoute = await calculateDetailedRoute(start, end, mode, departureTime);
    if (!detailedRoute) return null;
    
    return {
      mode: detailedRoute.mode,
      duration: detailedRoute.duration,
      distance: detailedRoute.distance,
      instructions: detailedRoute.steps.map(step => step.instruction).join(' → '),
    };
  } catch (error) {
    console.error('[Navigation] Error calculating route:', error);
    return null;
  }
};

/**
 * Calculate routes for all travel modes
 */
export const calculateAllRouteOptions = async (
  start: LocationCoordinates,
  end: LocationCoordinates
): Promise<RouteOption[]> => {
  const modes: TravelMode[] = isAggressiveMapboxCostMode()
    ? ['driving']
    : ['walking', 'driving', 'transit'];
  const results = await Promise.all(
    modes.map(async (mode) => {
      const route = await calculateRoute(start, end, mode);
      return route;
    })
  );

  return results.filter((route): route is RouteOption => route !== null);
};

/**
 * Find the next upcoming itinerary item
 */
export const findNextActivity = (
  itineraryItems: ItineraryItem[],
  currentTime: Date = new Date()
): ItineraryItem | null => {
  // Filter out completed and cancelled items
  const upcomingItems = itineraryItems.filter(
    (item) => item.status !== 'completed' && item.status !== 'cancelled'
  );

  // Sort by start date and time
  const sortedItems = upcomingItems.sort((a, b) => {
    const dateA = new Date(a.startDate);
    const dateB = new Date(b.startDate);

    // If same date, compare by time
    if (dateA.toDateString() === dateB.toDateString()) {
      const timeA = a.startTime ? parseTime(a.startTime) : 0;
      const timeB = b.startTime ? parseTime(b.startTime) : 0;
      return timeA - timeB;
    }

    return dateA.getTime() - dateB.getTime();
  });

  // Find first item that hasn't started yet
  for (const item of sortedItems) {
    const itemDateTime = getItemDateTime(item, currentTime);
    if (itemDateTime > currentTime) {
      return item;
    }
  }

  return null;
};

/**
 * Generate navigation prompt for next activity
 */
export const generateNavigationPrompt = async (
  currentLocation: LocationCoordinates,
  itineraryItems: ItineraryItem[]
): Promise<NavigationPrompt | null> => {
  const nextActivity = findNextActivity(itineraryItems);
  if (!nextActivity) {
    return null;
  }

  // Get coordinates for the activity
  let activityCoordinates: LocationCoordinates | null = null;

  if (nextActivity.address || nextActivity.location) {
    const address = nextActivity.address || nextActivity.location || '';
    
    // Try to get country code from itinerary item's trip context if available
    // This helps geocode activities in the correct country
    const geocodeOptions: { country?: string; proximity?: LocationCoordinates } = {};
    
    // If we have a trip destination, use it for context
    // Note: itineraryItems might have tripId, but we don't have direct access to trip here
    // For now, try to infer from address patterns (e.g., "Osaka, Japan")
    if (address.includes(',')) {
      const addressParts = address.split(',').map(s => s.trim());
      const lastPart = addressParts[addressParts.length - 1].toLowerCase();
      
      // Try to extract country code from address
      const { getCountryCodeFromDestination } = await import('@/utils/geocodeHelpers');
      const countryCode = getCountryCodeFromDestination(lastPart);
      if (countryCode) {
        geocodeOptions.country = countryCode;
        if (__DEV__) {
          console.log(`[Navigation] Using country code "${countryCode}" for geocoding activity: ${address}`);
        }
      }
    }
    
    const geocodeResult = await geocodeAddress(address, geocodeOptions);
    if (geocodeResult) {
      activityCoordinates = {
        latitude: geocodeResult.coordinates[1],
        longitude: geocodeResult.coordinates[0],
      };
      
      if (__DEV__) {
        console.log(`[Navigation] Geocoded activity "${nextActivity.title}" to: ${activityCoordinates.latitude}, ${activityCoordinates.longitude}`);
        
        // Validate the result makes sense
        if (geocodeOptions.country) {
          const resultCountry = geocodeResult.context?.find(ctx => ctx.id?.startsWith('country'))?.text?.toLowerCase();
          if (resultCountry) {
            const countryCodeMap: Record<string, string[]> = {
              'jp': ['japan', 'japanese'],
              'us': ['united states', 'usa'],
              'uk': ['united kingdom', 'britain'],
            };
            const expectedCountries = countryCodeMap[geocodeOptions.country.toLowerCase()] || [];
            const isCorrectCountry = expectedCountries.some(country => resultCountry.includes(country));
            
            if (!isCorrectCountry) {
              // This is just a warning - don't treat it as an error
              // The geocoding result might still be correct even if country text doesn't match exactly
              if (__DEV__) {
                console.warn(
                  `[Navigation] ⚠️ Geocoding country mismatch for "${nextActivity.title}": ` +
                  `Expected country code "${geocodeOptions.country}" (${expectedCountries.join(', ')}) but got "${resultCountry}". ` +
                  `Coordinates: ${activityCoordinates.latitude}, ${activityCoordinates.longitude}. ` +
                  `This is informational - coordinates may still be correct.`
                );
              }
            }
          }
        }
      }
    } else {
      if (__DEV__) {
        console.warn(`[Navigation] Failed to geocode activity "${nextActivity.title}" with address: ${address}`);
      }
    }
  }

  if (!activityCoordinates) {
    console.warn('[Navigation] Could not geocode activity location:', nextActivity);
    return null;
  }

  const distanceKm = haversineDistance(
    { lat: currentLocation.latitude, lng: currentLocation.longitude },
    { lat: activityCoordinates.latitude, lng: activityCoordinates.longitude },
  );
  if (distanceKm > MAX_NAV_PROMPT_KM) {
    if (__DEV__) {
      console.log(
        `[Navigation] Skipping prompt — ${distanceKm.toFixed(0)} km from next activity (testing away from trip?)`,
      );
    }
    return null;
  }

  // Calculate route options
  const routeOptions = await calculateAllRouteOptions(currentLocation, activityCoordinates);

  if (routeOptions.length === 0) {
    return null;
  }

  // Find the fastest option
  const fastestOption = routeOptions.reduce((prev, current) =>
    prev.duration < current.duration ? prev : current
  );

  // Calculate when to leave with adaptive buffer time
  const itemDateTime = getItemDateTime(nextActivity);
  
  // Adaptive buffer time: 
  // - Short trips (< 15 min): 5 min buffer
  // - Medium trips (15-60 min): 10 min buffer  
  // - Long trips (> 60 min): 15 min buffer
  // - Very long trips (> 2 hours): 20 min buffer
  const travelMinutes = fastestOption.duration / 60;
  let bufferMinutes = 5;
  if (travelMinutes > 120) {
    bufferMinutes = 20;
  } else if (travelMinutes > 60) {
    bufferMinutes = 15;
  } else if (travelMinutes > 15) {
    bufferMinutes = 10;
  }
  
  const bufferTime = bufferMinutes * 60;
  const recommendedDepartureTime = new Date(
    itemDateTime.getTime() - fastestOption.duration * 1000 - bufferTime * 1000,
  );

  const leaveInMinutes = Math.max(
    0,
    Math.round((recommendedDepartureTime.getTime() - Date.now()) / (1000 * 60)),
  );

  const travelDurationMinutes = Math.min(
    MAX_NAV_TRAVEL_MINUTES,
    Math.max(1, Math.round(fastestOption.duration / 60)),
  );
  const arrivalEta = new Date(Date.now() + fastestOption.duration * 1000);
  const message = buildPromptMessage({
    nextActivity,
    leaveInMinutes,
    travelDurationMinutes,
    itemDateTime,
    bufferMinutes,
  });

  return {
    nextActivity,
    currentLocation,
    routeOptions,
    recommendedDepartureTime,
    fastestRoute: fastestOption,
    travelDurationMinutes,
    leaveInMinutes,
    arrivalEta,
    activityStartDateTime: itemDateTime,
    bufferMinutes: Math.round(bufferTime / 60),
    message,
  };
};

/**
 * Helper: Parse time string (HH:mm) to minutes since midnight
 */
const parseTime = (timeString: string): number => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Helper: Get full date/time for an itinerary item
 */
const getItemDateTime = (item: ItineraryItem, referenceDate: Date = new Date()): Date => {
  const date = new Date(item.startDate);
  
  if (item.startTime && item.startTime !== 'flexible') {
    const [hours, minutes] = item.startTime.split(':').map(Number);
    date.setHours(hours, minutes, 0, 0);
  } else {
    // Default to 9 AM if no time specified or flexible
    date.setHours(9, 0, 0, 0);
  }

  return date;
};

/**
 * Calculate optimized multi-stop route
 * Optimizes the order of waypoints to minimize total travel time
 */
export const calculateOptimizedRoute = async (
  start: LocationCoordinates,
  waypoints: LocationCoordinates[],
  end: LocationCoordinates,
  mode: TravelMode = 'driving',
  departureTime?: Date
): Promise<DetailedRoute | null> => {
  if (waypoints.length === 0) {
    return calculateDetailedRoute(start, end, mode, departureTime);
  }

  try {
    if (isAggressiveMapboxCostMode()) {
      if (__DEV__) {
        console.warn('[Navigation] Optimized trips disabled in aggressive cost mode.');
      }
      return null;
    }
    // Mapbox Optimized Trips API limit: max 12 coordinates (includes start + end).
    // If exceeded, don't call the API (it will 422).
    const coordinateCount = waypoints.length + 2;
    if (coordinateCount > 12) {
      console.warn('[Navigation] Skipping route optimization: too many coordinates', {
        coordinateCount,
        max: 12,
      });
      return null;
    }

    const { getMapboxAccessTokenSync } = await import('@/config/mapbox');
    const accessToken = getMapboxAccessTokenSync();
    if (!accessToken) {
      console.warn('[Navigation] Mapbox access token missing; skipping optimized route calculation.');
      return null;
    }
    const profile = mode === 'transit' ? 'driving' : mode;
    const optimizationBudget = tryConsumeMapboxBudget('optimization');
    if (!optimizationBudget.allowed) {
      if (__DEV__) {
        console.warn('[Navigation] Optimization blocked by cost policy:', optimizationBudget.reason);
      }
      return null;
    }

    // Build waypoints string: start;waypoint1;waypoint2;...;end
    const coordinates = [
      `${start.longitude},${start.latitude}`,
      ...waypoints.map((wp) => `${wp.longitude},${wp.latitude}`),
      `${end.longitude},${end.latitude}`,
    ].join(';');

    const encodedToken = encodeURIComponent(accessToken);
    // IMPORTANT: Optimization requires the Optimized Trips API (not the Directions API).
    // https://docs.mapbox.com/api/navigation/optimized-trips/
    // Note: This endpoint does not support traffic departure times. We ignore `departureTime`.
    const url = `https://api.mapbox.com/optimized-trips/v1/mapbox/${profile}/${coordinates}?access_token=${encodedToken}&geometries=geojson&steps=true&overview=full&roundtrip=false&source=first&destination=last`;

    const response = await fetch(url);
    if (!response.ok) {
      let bodyText = '';
      try {
        bodyText = await response.text();
      } catch {
        // ignore
      }
      let apiMessage = '';
      try {
        const parsed = bodyText ? JSON.parse(bodyText) : null;
        apiMessage = parsed?.message || parsed?.error || '';
      } catch {
        apiMessage = bodyText || '';
      }
      throw new Error(
        `Route optimization failed (${response.status}): ${apiMessage || response.statusText || 'Unknown error'}`,
      );
    }

    const data = await response.json();

    if (data.trips && data.trips.length > 0) {
      const trip = data.trips[0];
      const legs = trip.legs || [];

      // Combine geometry from all legs
      const geometry: LocationCoordinates[] = [];
      const steps: RouteStep[] = [];

      legs.forEach((leg: any) => {
        // Extract geometry
        const coords = leg.geometry?.coordinates || leg.steps?.[0]?.geometry?.coordinates;
        if (coords && Array.isArray(coords)) {
          coords.forEach((coord: [number, number]) => {
            geometry.push({
              latitude: coord[1],
              longitude: coord[0],
            });
          });
        }

        // Extract steps
        if (leg.steps && Array.isArray(leg.steps)) {
          leg.steps.forEach((step: any) => {
            const instruction =
              step.maneuver?.instruction ||
              step.maneuver?.modifier + ' ' + step.maneuver?.type ||
              step.maneuver?.type ||
              'Continue';

            steps.push({
              instruction: instruction.trim() || 'Continue',
              distance: step.distance || 0,
              duration: step.duration || 0,
              maneuver: {
                type: step.maneuver?.type || 'turn',
                location:
                  step.maneuver?.location ||
                  (step.geometry?.coordinates?.[0]
                    ? [step.geometry.coordinates[0][0], step.geometry.coordinates[0][1]]
                    : [0, 0]),
                instruction: step.maneuver?.instruction,
              },
            });
          });
        }
      });

      const totalDuration = legs.reduce((sum: number, leg: any) => sum + (leg.duration || 0), 0);
      const totalDistance = legs.reduce((sum: number, leg: any) => sum + (leg.distance || 0), 0);

      return {
        mode,
        duration: totalDuration,
        distance: totalDistance,
        geometry,
        steps,
        summary: `${Math.round(totalDistance / 1000)} km, ${Math.round(totalDuration / 60)} min`,
      };
    }

    return null;
  } catch (error) {
    recordMapboxCostError('optimization');
    console.error('[Navigation] Error calculating optimized route:', error);
    return null;
  }
};

/**
 * Optimize waypoint order to minimize total travel time
 * Uses a simple nearest-neighbor heuristic for now
 */
export const optimizeWaypointOrder = async (
  start: LocationCoordinates,
  waypoints: LocationCoordinates[],
  end: LocationCoordinates,
  mode: TravelMode = 'driving'
): Promise<LocationCoordinates[]> => {
  if (waypoints.length <= 1) {
    return waypoints;
  }

  // Simple nearest-neighbor optimization
  const optimized: LocationCoordinates[] = [];
  const remaining = [...waypoints];
  let current = start;

  while (remaining.length > 0) {
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    // Find nearest waypoint to current position
    for (let i = 0; i < remaining.length; i++) {
      const distance = calculateHaversineDistance(current, remaining[i]);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = i;
      }
    }

    optimized.push(remaining[nearestIndex]);
    current = remaining[nearestIndex];
    remaining.splice(nearestIndex, 1);
  }

  return optimized;
};

/**
 * Calculate Haversine distance between two points (in meters)
 */
function calculateHaversineDistance(
  point1: LocationCoordinates,
  point2: LocationCoordinates
): number {
  const R = 6371000; // Earth radius in meters
  const dLat = ((point2.latitude - point1.latitude) * Math.PI) / 180;
  const dLon = ((point2.longitude - point1.longitude) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((point1.latitude * Math.PI) / 180) *
      Math.cos((point2.latitude * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Helper: Format time until an event
 */
const formatTimeUntil = (targetDate: Date): string => {
  const now = new Date();
  const diffMs = targetDate.getTime() - now.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  } else if (diffMins > 0) {
    return `${diffMins} minute${diffMins > 1 ? 's' : ''}`;
  } else {
    return 'soon';
  }
};

/**
 * Cache route for offline navigation
 */
export const cacheRouteForOffline = async (
  route: DetailedRoute,
  routeId: string
): Promise<void> => {
  try {
    const { offlineStorage } = await import('./offlineStorage');
    await offlineStorage.set(`@navigation_route_${routeId}`, route, 7 * 24 * 60 * 60 * 1000); // 7 days
    console.log(`[Navigation] Cached route ${routeId} for offline use`);
  } catch (error) {
    console.error('[Navigation] Error caching route:', error);
  }
};

/**
 * Get cached route for offline navigation
 */
export const getCachedRoute = async (routeId: string): Promise<DetailedRoute | null> => {
  try {
    const { offlineStorage } = await import('./offlineStorage');
    const route = await offlineStorage.get<DetailedRoute>(`@navigation_route_${routeId}`);
    return route || null;
  } catch (error) {
    console.error('[Navigation] Error getting cached route:', error);
    return null;
  }
};

/**
 * Calculate route with offline fallback
 */
export const calculateRouteWithOfflineFallback = async (
  start: LocationCoordinates,
  end: LocationCoordinates,
  mode: TravelMode = 'driving',
  departureTime?: Date,
  routeId?: string
): Promise<DetailedRoute | null> => {
  // Try online first
  try {
    const route = await calculateDetailedRoute(start, end, mode, departureTime);
    if (route && routeId) {
      await cacheRouteForOffline(route, routeId);
    }
    return route;
  } catch (error) {
    console.warn('[Navigation] Online route calculation failed, trying offline cache:', error);
    
    // Fallback to cached route if available
    if (routeId) {
      const cachedRoute = await getCachedRoute(routeId);
      if (cachedRoute) {
        console.log('[Navigation] Using cached route for offline navigation');
        return cachedRoute;
      }
    }
    
    throw error;
  }
};

const formatMinutesCompact = (minutes: number): string => {
  if (minutes >= 1440) {
    const days = Math.floor(minutes / 1440);
    const hours = Math.round((minutes % 1440) / 60);
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return `${minutes}min`;
};

const buildPromptMessage = ({
  nextActivity,
  leaveInMinutes,
  travelDurationMinutes,
  itemDateTime,
  bufferMinutes,
}: {
  nextActivity: ItineraryItem;
  leaveInMinutes: number;
  travelDurationMinutes: number;
  itemDateTime: Date;
  bufferMinutes: number;
}): string => {
  const activityWindow = formatTimeUntil(itemDateTime);
  let prefix = `Your next activity "${nextActivity.title}" is in ${activityWindow}. `;

  if (leaveInMinutes > 0) {
    prefix += `Head out in ${formatMinutesCompact(leaveInMinutes)}`;
  } else {
    prefix += 'Leave now';
  }

  prefix += ` — about ${formatMinutesCompact(travelDurationMinutes)} of travel plus a ${bufferMinutes}min buffer.`;
  return prefix;
};

/**
 * Monitor departure from a start location to trigger navigation recalculation/alerts.
 * Useful for confirming the user has actually started moving toward their next stop.
 */
export const startDepartureGeofence = ({
  origin,
  tripId,
  radiusMeters = 80,
  dwellMinutes = 1,
  onEvent,
  onDepart,
  onError,
}: {
  origin: LocationCoordinates;
  tripId?: string;
  radiusMeters?: number;
  dwellMinutes?: number;
  onEvent?: (event: GeofenceEvent) => void;
  onDepart?: (event: GeofenceEvent) => void;
  onError?: (error: Error) => void;
}): (() => void) => {
  const geofence = buildPointGeofence({
    id: `departure-${tripId ?? 'current'}`,
    name: 'Departure Zone',
    category: 'custom',
    coordinates: origin,
    radiusMeters,
    dwellMinutes,
    tripId,
  });

  return startGeofenceMonitoring(
    [geofence],
    {
      onEvent: (event) => {
        onEvent?.(event);
        if (event.type === 'exit') {
          onDepart?.(event);
        }
      },
      onError,
    },
    {
      sampleIntervalMs: 8_000,
      distanceIntervalMeters: 15,
      defaultDwellMinutes: dwellMinutes,
    },
  );
};

